import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import moment from "moment-timezone";

const columnsSimple = (
  theme,
  throwMessage,
  idTradeGenerator,
  runDurationCal,
  handleOpenJournal,
  auth,
) => {
  // Helper function to get value based on equity type
  const getValue = (row, pcField, icField) => {
    if (
      row.equityType === "Personal Capital" ||
      row.equityType === "Backtest"
    ) {
      return row[pcField];
    } else {
      return row[icField];
    }
  };

  // Helper function to convert date/time to selected timezone
  const formatDateTimeInTimezone = (dateStr, timeStr, originalTimezone) => {
    if (!dateStr) return "";

    try {
      // Get the user's selected timezone from settings
      const storedTimezone = localStorage.getItem("economic-calendar-timezone");
      const targetTimezone =
        storedTimezone && storedTimezone !== "local"
          ? storedTimezone
          : Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Parse the date/time in the original timezone the trade was taken in
      const dateTimeStr = timeStr ? `${dateStr} ${timeStr}` : dateStr;
      const originalMoment = moment.tz(
        dateTimeStr,
        "DD/MM/YY HH:mm",
        originalTimezone || "UTC",
      );

      // Convert to the selected timezone
      const convertedMoment = originalMoment.clone().tz(targetTimezone);

      // Format for display
      const formattedDate = convertedMoment.format("DD/MM/YY");
      const formattedTime = convertedMoment.format("HH:mm");

      return timeStr ? `${formattedDate} ${formattedTime}` : formattedDate;
    } catch (error) {
      console.error("Error converting timezone:", error);
      // Fallback to original values if conversion fails
      return timeStr ? `${dateStr} ${timeStr}` : dateStr;
    }
  };

  let array = [
    {
      field: "pair",
      headerName: "Instrument",
      width: 135,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        if (!params.row.pair) {
          return "";
        }
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            <p
              style={{
                cursor: "pointer",
                color: params.row.journalComplete
                  ? theme[0] === "dark"
                    ? "#FCFCFF"
                    : "black"
                  : "#f23645",
                fontWeight: "400",
                margin: 0,
              }}
              onClick={() => {
                if (!params.row.journalID) {
                  throwMessage("error", "No Journal Found");
                  handleOpenJournal(params.row, true);
                } else {
                  handleOpenJournal(params.row, true);
                }
              }}
            >
              {params.row.pair}
            </p>
            {params.row.foreCastId ? (
              <span
                style={{
                  cursor: "pointer",
                  backgroundColor: "#26a65d",
                  color: "white",
                  fontSize: "10px",
                  padding: "1px 4px",
                  borderRadius: "3px",
                  fontWeight: "600",
                }}
                onClick={() => {
                  localStorage.setItem(
                    "forecast-to-view",
                    params.row.foreCastId,
                  );
                  localStorage.setItem("screen", 0);
                  auth.setTab(0);
                  localStorage.setItem("from-tracker", true);
                  // Save the current filter state before opening forecast
                  localStorage.setItem(
                    "tracker-trade-filter-before-forecast",
                    localStorage.getItem("trade-to-view") || "",
                  );
                  localStorage.setItem(
                    "tracker-linked-filter-before-forecast",
                    localStorage.getItem("linked-trade-ids") || "",
                  );
                }}
              >
                F
              </span>
            ) : (
              <span
                style={{
                  cursor: "pointer",
                  backgroundColor: "#f23645",
                  color: "white",
                  fontSize: "10px",
                  padding: "1px 4px",
                  borderRadius: "3px",
                  fontWeight: "600",
                }}
                onClick={() => {
                  throwMessage("info", "No Forecast Attached");
                }}
              >
                F
              </span>
            )}
          </div>
        );
      },
    },

    {
      field: "tradingStyle",
      headerName: "Style",
      width: 102,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        if (!params.row.tradingStyle) {
          return "";
        }
        return (
          <p
            style={{
              color: theme[0] === "dark" ? "#FCFCFF" : "black",
              fontSize: "13px",
            }}
          >
            {params.row.tradingStyle}
          </p>
        );
      },
    },

    {
      field: "direction",
      headerName: "Direction",
      width: 102,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        if (!params.row.direction) {
          return "";
        }
        return (
          <p
            style={{
              color:
                params.row.direction === "Short"
                  ? "#db0671"
                  : params.row.direction === "Long"
                    ? "#0079cf"
                    : "",
            }}
          >
            {params.row.direction}
          </p>
        );
      },
    },

    {
      field: "outcome",
      headerName: "Outcome",
      width: 102,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const outcome = getValue(params.row, "outcomePC", "outcomeIC");

        if (!outcome) {
          return (
            <HorizontalRuleIcon
              sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}
            />
          );
        }
        return (
          <p
            style={{
              color:
                outcome === "Loss"
                  ? "#f23645"
                  : outcome === "Win"
                    ? "#26a65d"
                    : outcome === "Breakeven"
                      ? "#ED9E0E"
                      : "",
            }}
          >
            {outcome}
          </p>
        );
      },
    },

    {
      field: "grade",
      headerName: "Grade",
      width: 124,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        if (!params.row.grade) {
          return "";
        }
        return (
          <p
            style={{
              color:
                params.row.grade === "Low Risk"
                  ? "#26a65d"
                  : params.row.grade === "High Risk"
                    ? "#FFA500"
                    : params.row.grade === "Invalid"
                      ? "#f23645"
                      : "#f23645",
            }}
          >
            {params.row.grade}
          </p>
        );
      },
    },

    {
      field: "profitLossDollar",
      headerName: "P/L Gross",
      width: 136,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const profitLoss = getValue(
          params.row,
          "profitLossDollarPersonal",
          "profitLossDollarInvestor",
        );

        if (!profitLoss) {
          return <p style={{ color: "#ED9E0E" }}>$0</p>;
        }
        return (
          <p
            style={{
              color:
                profitLoss < 0
                  ? "#f23645"
                  : profitLoss > 0
                    ? "#26a65d"
                    : "#ED9E0E",
            }}
          >
            ${Math.abs(profitLoss).toFixed(2)}
          </p>
        );
      },
    },

    {
      field: "profitLossPercentage",
      headerName: "% Gross",
      width: 136,
      valueFormatter: ({ value }) => `${value} %`,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const percentage = getValue(
          params.row,
          "profitLossPercentagePC",
          "profitLossPercentageIC",
        );

        if (!percentage) {
          return <p style={{ color: "#ED9E0E" }}>0 %</p>;
        }
        return (
          <p
            style={{
              color:
                percentage < 0
                  ? "#f23645"
                  : percentage > 0
                    ? "#26a65d"
                    : "#ED9E0E",
            }}
          >
            {Math.abs(percentage).toFixed(2)} %
          </p>
        );
      },
    },

    {
      field: "entryDateTime",
      headerName: "Entry (Local)",
      width: 146,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const d = params.row.entryDate;
        const t = params.row.entryTime;
        const tz = params.row.timezone;
        if (!d && !t) {
          return "";
        }
        const convertedDateTime = formatDateTimeInTimezone(d, t, tz);
        return <p>{convertedDateTime}</p>;
      },
    },

    {
      field: "duration",
      headerName: "Duration",
      width: 70,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        // Calculate duration from entry and exit dates/times
        const entryDate = params.row.entryDate;
        const entryTime = params.row.entryTime;
        const exitDate = params.row.exitDate;
        const exitTime = params.row.exitTime;

        if (!entryDate || !entryTime || !exitDate || !exitTime) {
          return <span>-</span>;
        }

        // Parse dates in DD/MM/YY format (non-American)
        const parseDate = (dateStr) => {
          const parts = dateStr.split("/");
          if (parts.length !== 3) return null;
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1; // JavaScript months are 0-indexed
          const year =
            parts[2].length === 2
              ? 2000 + parseInt(parts[2], 10)
              : parseInt(parts[2], 10);
          return new Date(year, month, day);
        };

        const entryDateTime = parseDate(entryDate);
        const exitDateTime = parseDate(exitDate);

        if (
          !entryDateTime ||
          !exitDateTime ||
          isNaN(entryDateTime.getTime()) ||
          isNaN(exitDateTime.getTime())
        ) {
          return <span>-</span>;
        }

        // Parse time (HH:MM format)
        const parseTime = (timeStr) => {
          const parts = timeStr.split(":");
          if (parts.length !== 2) return 0;
          return parseInt(parts[0], 10) + parseInt(parts[1], 10) / 60;
        };

        const entryHours = parseTime(entryTime);
        const exitHours = parseTime(exitTime);

        entryDateTime.setHours(entryHours);
        exitDateTime.setHours(exitHours);

        const diffMs = exitDateTime - entryDateTime;
        const diffHours = diffMs / (1000 * 60 * 60);

        if (diffHours === 0 || isNaN(diffHours)) {
          return <span>-</span>;
        }

        const days = (diffHours / 24).toFixed(1);

        return <p>{days > 1 ? days + "d" : diffHours.toFixed(1) + "h"}</p>;
      },
    },

    {
      field: "entryType",
      headerName: "Entry Type",
      width: 146,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        if (!params.row.entryType) {
          return "";
        }
        return <p>{params.row.entryType}</p>;
      },
    },

    {
      field: "entryTimeframe",
      headerName: "Entry TF",
      width: 70,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        if (!params.row.entryTimeframe) {
          return "";
        }
        return <p>{params.row.entryTimeframe}</p>;
      },
    },

    {
      field: "betaPattern",
      headerName: "Beta Pattern",
      width: 194,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        if (!params.row.betaPattern) {
          return "";
        }
        return <p>{params.row.betaPattern}</p>;
      },
    },
  ];

  return array;
};

export default columnsSimple;
